import {
  Text,
  Flex,
  Spinner,
  Divider,
  Tabs,
  TabList,
  TabPanels,
  TabPanel,
} from "@chakra-ui/react";
import { useQuery, skipToken } from "@tanstack/react-query";
import { Bundle, Comic, Release } from "../../../types";
import { getComicReleases, getRelease } from "../hooks/useSlideInMenu";
import { useEffect, useRef } from "react";
import { ReleaseInfoHeader } from "./ReleaseInfoHeader";
import { ReleaseInfoCoverCarousel } from "./ReleaseInfoCoverCarousel";
import { ReleaseInfoPageCarousel } from "./ReleaseInfoPageCarousel";
import { ShowMoreSection } from "../../containers/ShowMoreSection";
import { ArtistSection } from "../comicDetails/ArtistSection";
import { ComicContributorList } from "../comicDetails/ComicDescription";
import { ComicLabelsCarousel } from "../../comicLabels/ComicLabelsCarousel";
import { FunnelConfigTypes, FunnelContextArea } from "../../funnel/types";
import { useEntityCache } from "../../../hooks/useEntityCache";
import { ReleaseInfoActions } from "./ReleaseInfoActions";
import { UnderlinedTab } from "../../tabs/UnderlinedTab";
import { NewBadge } from "../../buttons/NewBadge";
import { SlideInMenuBundlesList } from "../components/SlideInMenuBundlesList";
import { t } from "i18next";
import { trackReleaseDetailView } from "../../../analytics/events/navigation";
import { mapDetailSource } from "../../../analytics/params/navigation";
import { getNavigationAnalyticsAttribution } from "../../../analytics/params/navigationContextAttribution";
import { shouldTrackDetailViewForMenuInstance } from "../../../analytics/detailViewRegistry";
import { ReaderNavigationContext } from "../../../screens/reader/types";

interface ReleaseInfoContentProps {
  releaseUuid: string;
  navigationContext?: ReaderNavigationContext;
  menuInstanceId?: string;
  /**
   * Slide-in passes its setFunnelConfig (paywall takes over the slide-in body).
   * Landing can pass undefined or a full-page funnel handler.
   */
  setFunnelConfig?: (config?: FunnelConfigTypes) => void;
  /**
   * Slide-in passes openReleaseInfoMenu so the cover carousel can swap the
   * active release without remounting. Landing can pass a navigate-based
   * equivalent.
   */
  onSelectRelease?: (releaseUuid: string, comicSlug?: string) => void;
  /**
   * Optional paywall opener — called by ReleaseInfoActions for buy/subscribe.
   */
  openPaywall?: (config: FunnelConfigTypes) => void;
}

export function ReleaseInfoContent({
  releaseUuid,
  navigationContext,
  menuInstanceId,
  setFunnelConfig,
  onSelectRelease,
  openPaywall,
}: ReleaseInfoContentProps) {
  const { saveEntityToCache } = useEntityCache();

  const { data: fetchedReleaseResponse, isLoading } = useQuery({
    enabled: !!releaseUuid,
    queryKey: ["releaseDetailsRelease", releaseUuid],
    networkMode: "offlineFirst",
    queryFn: async () => {
      const response = await getRelease(releaseUuid, navigationContext);
      if (response?.data.payload.results) {
        saveEntityToCache(response.data.payload.results);
      }
      return response;
    },
  });

  const fetchedRelease = fetchedReleaseResponse?.data.payload.results;
  const { data: fetchedComicReleasesResponse, isLoading: isLoadingReleases } =
    useQuery({
      enabled: !!fetchedRelease?.comic?.slug,
      queryKey: ["fetchedComicReleases", fetchedRelease?.comic?.slug],
      networkMode: "offlineFirst",
      placeholderData: (prev) => prev,
      queryFn: () => {
        return getComicReleases(
          fetchedRelease?.comic?.slug ?? "",
          fetchedRelease?.lang_id,
        );
      },
    });

  const cacheKey = `${fetchedRelease?.entity_type}-${fetchedRelease?.id}`;
  const { data: activeRelease } = useQuery<Release>({
    enabled: !!fetchedRelease,
    queryKey: [cacheKey],
    queryFn: skipToken,
    networkMode: "offlineFirst",
  });

  const releases = fetchedComicReleasesResponse?.data.payload.results;

  const bundles: Bundle[] = activeRelease?.bundles
    ? activeRelease.bundles.map((bundle) => bundle as unknown as Bundle)
    : [];

  const hasBundles = bundles.length > 0;

  const lastTrackedReleaseId = useRef<number | undefined>();
  const comic = (activeRelease?.comic ?? fetchedRelease?.comic) as Comic;

  useEffect(() => {
    if (
      !activeRelease?.id ||
      !activeRelease?.comic?.id ||
      lastTrackedReleaseId.current === activeRelease.id
    ) {
      return;
    }

    if (
      !shouldTrackDetailViewForMenuInstance(
        menuInstanceId,
        "release",
        activeRelease.id,
      )
    ) {
      lastTrackedReleaseId.current = activeRelease.id;
      return;
    }

    const source = mapDetailSource(navigationContext);
    const { impressionId, searchQueryId } =
      getNavigationAnalyticsAttribution(navigationContext);
    trackReleaseDetailView({
      comicId: activeRelease.comic.id,
      releaseId: activeRelease.id,
      source,
      impressionId,
      searchQueryId,
      searchResultPosition: navigationContext?.cardPosition,
      referrerUrl:
        source === "deep_link" ? document.referrer || undefined : undefined,
    });

    lastTrackedReleaseId.current = activeRelease.id;
  }, [
    activeRelease?.id,
    activeRelease?.comic?.id,
    menuInstanceId,
    navigationContext,
  ]);

  return (
    <>
      {releases && (
        <ReleaseInfoCoverCarousel
          releases={releases}
          activeReleaseUuid={activeRelease?.key}
          setActiveReleaseUuid={(uuid) => {
            const next = releases.find((r) => r.key === uuid);
            if (next) {
              onSelectRelease?.(
                uuid,
                next.comic?.slug ?? next.comic_slug,
              );
            }
          }}
        />
      )}
      {!activeRelease || isLoading || isLoadingReleases ? (
        <Flex
          justifyContent="center"
          alignItems="center"
          minHeight="100vh"
          width="100%"
          position="absolute"
          top="0"
          left="0"
        >
          <Spinner color="blaze.blaze" />
        </Flex>
      ) : (
        <Flex
          flexDirection="column"
          pt="16px"
          px="24px"
          pb="24px"
          gap="16px"
        >
          <ReleaseInfoHeader
            comic={comic}
            release={activeRelease as Release}
          />
          <Divider color="transparent.white.10" mt="8px" />
          <ReleaseInfoActions
            setFunnelConfig={openPaywall ?? setFunnelConfig ?? (() => {})}
            contextArea={FunnelContextArea.releaseDetails}
            release={activeRelease as Release}
          />
          <Divider color="transparent.white.10" />
          <ReleaseInfoPageCarousel release={activeRelease as Release} />
          {hasBundles ? (
            <Tabs variant="line" colorScheme="blaze">
              <TabList>
                <UnderlinedTab fontSize="16px">
                  {t("components.slideInMenu.bundleDetails.details")}
                </UnderlinedTab>
                <UnderlinedTab fontSize="16px">
                  <Flex alignItems="center" gap={2}>
                    <Text>
                      {t("components.slideInMenu.bundleDetails.bundles")}
                    </Text>
                    <NewBadge />
                  </Flex>
                </UnderlinedTab>
              </TabList>
              <TabPanels>
                <TabPanel px={0}>
                  <ReleaseDetailsBody comic={comic} activeRelease={activeRelease} />
                </TabPanel>
                <TabPanel px={0}>
                  <SlideInMenuBundlesList
                    bundles={bundles}
                    text={t(
                      "components.slideInMenu.comicDetailsMenu.bundlesContainingComic",
                    )}
                  />
                </TabPanel>
              </TabPanels>
            </Tabs>
          ) : (
            <ReleaseDetailsBody comic={comic} activeRelease={activeRelease} />
          )}
        </Flex>
      )}
    </>
  );
}

function ReleaseDetailsBody({
  comic,
  activeRelease,
}: {
  comic: Comic;
  activeRelease: Release;
}) {
  return (
    <Flex flexDirection="column" gap="16px">
      <ComicLabelsCarousel comicData={comic} />
      <Divider color="transparent.white.10" />
      <ShowMoreSection collapsedHeight={150} expandedMarginBottom="24px">
        <Text
          fontFamily="Roboto"
          fontSize="14px"
          fontWeight="400"
          lineHeight="22.4px"
          color="neutral.300"
        >
          {activeRelease.description}
        </Text>
      </ShowMoreSection>
      {comic.artist && (
        <ArtistSection
          artist={comic.artist}
          contextArea="release-details"
        />
      )}
      {comic.contributors && (
        <>
          <Divider color="transparent.white.10" />
          <ComicContributorList comicData={comic} byRole />
        </>
      )}
    </Flex>
  );
}
